// Calendar dates stay dates; timestamp display follows the signed-in user's
// preference (device zone/locale by default). Expiration is calculated by the API.
const CHICAGO_TZ = "America/Chicago";
const DASH = "—";
const isDateOnly = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
let preferences = { display_time_zone: "", date_format: "locale" };

export function configureDisplayPreferences(value = {}) {
  preferences = {
    display_time_zone: value.display_time_zone || "",
    date_format: ["locale", "mdy", "dmy", "iso"].includes(value.date_format) ? value.date_format : "locale",
  };
}

export function displayPreferences() {
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  let timeZone = preferences.display_time_zone || deviceZone;
  try { new Intl.DateTimeFormat("en", { timeZone }); } catch { timeZone = deviceZone; }
  const locale = typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";
  return { timeZone, locale, dateFormat: preferences.date_format };
}

function calendarDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  if (!s) return null;
  if (isDateOnly(s)) {
    // UTC is only a neutral calendar anchor, never an expiry instant.
    const date = new Date(`${s}T12:00:00Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== s) return null;
    return { date, timeZone: "UTC" };
  }
  const date = new Date(s);
  return Number.isNaN(date.getTime()) ? null : { date, timeZone: displayPreferences().timeZone };
}

export function formatBusinessDate(value) {
  const parsed = calendarDate(value);
  if (!parsed) return DASH;
  const { locale, dateFormat } = displayPreferences();
  const options = { month: "2-digit", day: "2-digit", year: "numeric", timeZone: parsed.timeZone };
  if (dateFormat === "locale") return parsed.date.toLocaleDateString(locale, options);
  const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(parsed.date);
  const part = (type) => parts.find((p) => p.type === type).value;
  if (dateFormat === "iso") return `${part("year")}-${part("month")}-${part("day")}`;
  return dateFormat === "dmy"
    ? `${part("day")}/${part("month")}/${part("year")}`
    : `${part("month")}/${part("day")}/${part("year")}`;
}

export function formatBusinessDates(value) {
  if (value === null || value === undefined || value === "") return DASH;
  return String(value).split(" · ").map(formatBusinessDate).join(" · ");
}

export function formatTimestamp(value, timeZoneOverride) {
  if (value === null || value === undefined || value === "") return DASH;
  const s = String(value).trim();
  if (isDateOnly(s)) return formatBusinessDate(s);
  const date = new Date(s);
  if (!s || Number.isNaN(date.getTime())) return DASH;
  const { timeZone: preferredZone, locale } = displayPreferences();
  const timeZone = timeZoneOverride || preferredZone;
  const dateParts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const part = (type) => dateParts.find((p) => p.type === type).value;
  const day = formatBusinessDate(`${part("year")}-${part("month")}-${part("day")}`);
  const time = date.toLocaleTimeString(locale, {
    hour: "numeric", minute: "2-digit", timeZone,
    // CT remains familiar for Texas; other zones use the browser's actual label.
    ...(timeZone === CHICAGO_TZ ? { hour12: true } : { timeZoneName: "short" }),
  });
  return `${day} ${time}${timeZone === CHICAGO_TZ ? " CT" : ""}`;
}

export function todayInTimeZone(timeZone = CHICAGO_TZ) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const part = (type) => parts.find((p) => p.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

// Legacy company-wide business-date callers retain their existing Chicago rule.
export function todayInCentralTime() { return todayInTimeZone(CHICAGO_TZ); }

export function formatTimeOfDay(value) {
  if (!value || isDateOnly(String(value).trim())) return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  const { timeZone, locale } = displayPreferences();
  return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", timeZone,
    ...(timeZone === CHICAGO_TZ ? { hour12: true } : {}) });
}

export function formatLocateExpiration(record) {
  if (record.expires_at) return `Expires ${formatTimestamp(record.expiration_at || record.expires_at, record.job_site_time_zone)} (${record.job_site_time_zone || "ticket time zone"})`;
  return record.expires_on ? `Valid through ${formatBusinessDate(record.expires_on)}` : "Expiration not set";
}

const FALLBACK_ZONES = ["Pacific/Honolulu", "America/Anchorage", "America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York", "America/Halifax", "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Moscow", "Africa/Cairo", "Asia/Dubai", "Asia/Kolkata", "Asia/Shanghai", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland", "UTC"];

function zoneLabel(zone, now) {
  const part = (style) => {
    try { return new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: style }).formatToParts(now).find((p) => p.type === "timeZoneName")?.value; } catch { return ""; }
  };
  const long = part("long");
  const offset = part("shortOffset");
  const city = zone.split("/").slice(1).join(" / ").replace(/_/g, " ") || zone;
  return [city, long, offset && `(${offset})`].filter(Boolean).join(" · ");
}

// Every IANA zone the device supports, plus extras (e.g. a saved/device zone), with readable labels.
export function timeZoneOptions(extra = []) {
  const supported = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : FALLBACK_ZONES;
  const names = new Set([...supported, "UTC", ...extra.filter(Boolean)]);
  const now = new Date();
  return [...names].sort().map((value) => ({ value, label: zoneLabel(value, now) }));
}
